
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useSearchParams } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import PageTransition from "./components/PageTransition";
import { AuthProvider } from "./context/AuthContext";
import ProverbWidget from "./components/ProverbWidget";
import ExternalRedirect from "./components/utility/ExternalRedirect";
import { lazy, Suspense, useEffect } from "react";

const queryClient = new QueryClient();

const HomePage = lazy(() => import("./pages/HomePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const MissionPage = lazy(() => import("./pages/MissionPage"));
const GetInvolvedPage = lazy(() => import("./pages/GetInvolvedPage"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const NewsDetailPage = lazy(() => import("./pages/NewsDetailPage"));
const LibraryArchivesPage = lazy(() => import("./pages/LibraryArchivesPage"));
const LearningPathDetailPage = lazy(() => import("./pages/LearningPathDetailPage"));
const AcademyRoomPage = lazy(() => import("./pages/AcademyRoomPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const JoinPage = lazy(() => import("./pages/JoinPage"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TopicDetail = lazy(() => import("./components/community/Hub/TopicDetail"));
const AuthVerifyPage = lazy(() => import("./pages/AuthVerifyPage"));
const ProfileSharePage = lazy(() => import("./pages/ProfileSharePage"));

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-gray-950">
    <div className="h-10 w-10 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
  </div>
);

// Global redirect function - can be called from anywhere
export const redirectToOmoduduwa = () => {
  console.log("GLOBAL REDIRECT: Redirecting to omooduduwa.org/join");
  window.location.replace('https://www.omooduduwa.org/join');
};

// Immediate execution to check URLs before React even loads
(function() {
  try {
    const currentUrl = window.location.href;
    const pathname = window.location.pathname || '';
    
    // If the URL contains our target pattern, redirect immediately
    if (currentUrl && currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195')) {
      console.log("IMMEDIATE EXECUTION: Special URL pattern found, redirecting now");
      window.location.replace('https://www.omooduduwa.org/join');
    }
    
    // Also check URL parameters directly
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = urlParams.get('referrer');
    if (referrer === '21d11b63-7b91-46fa-8c91-a30744574195') {
      console.log("IMMEDIATE EXECUTION: Special referrer parameter found, redirecting now");
      window.location.replace('https://www.omooduduwa.org/join');
    }
    
    // Additional check for the exact path /join with our ID
    if (pathname === '/join' && 
        (referrer === '21d11b63-7b91-46fa-8c91-a30744574195' || 
         currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195'))) {
      console.log("IMMEDIATE EXECUTION: Join page with special ID detected, redirecting now");
      window.location.replace('https://www.omooduduwa.org/join');
    }
  } catch (e) {
    console.error("Error in immediate execution check:", e);
  }
})();

// Root level redirect handler for special URLs and referrer IDs
const RootRedirectHandler = ({ children }: { children: React.ReactNode }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const referrer = searchParams.get('referrer');
  
  // Check for special redirect case immediately
  useEffect(() => {
    const currentUrl = window.location.href;
    const pathname = location.pathname;
    
    console.log("RootRedirectHandler: Current URL:", currentUrl);
    console.log("RootRedirectHandler: Current pathname:", pathname);
    
    // Handle the direct URL case
    if (currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195')) {
      console.log("RootRedirectHandler: Special URL pattern detected. Redirecting now.");
      redirectToOmoduduwa();
      return;
    } 
    // Handle the referrer parameter case
    else if (referrer === '21d11b63-7b91-46fa-8c91-a30744574195') {
      console.log("RootRedirectHandler: Special referrer parameter detected. Redirecting now.");
      redirectToOmoduduwa();
      return;
    }
    // Handle the specific join page case
    else if (pathname === '/join' && 
            (referrer === '21d11b63-7b91-46fa-8c91-a30744574195' || 
             currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195'))) {
      console.log("RootRedirectHandler: Join page with special ID detected. Redirecting now.");
      redirectToOmoduduwa();
      return;
    }
  }, [referrer, location]);
  
  // Define special redirect mappings for specific referrer IDs
  const specialRedirectMappings = [
    {
      param: 'referrer',
      value: '21d11b63-7b91-46fa-8c91-a30744574195',
      url: 'https://www.omooduduwa.org/join',
      // Don't specify full domain, just the path pattern to match
      fullPath: '/join?referrer=21d11b63-7b91-46fa-8c91-a30744574195'
    }
  ];
  
  return (
    <ExternalRedirect
      paramName="referrer"
      redirectMappings={specialRedirectMappings}
      immediate={true}
      checkFullPath={true}
    >
      {children}
    </ExternalRedirect>
  );
};

const AppContent = () => {
  const location = useLocation();
  // Hide Navbar and Footer for the immersive Academy Room experience
  const isImmersiveRoute = location.pathname.startsWith('/academy/room/');

  // Check for direct URL match at the app level
  useEffect(() => {
    const currentUrl = window.location.href;
    if (currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195')) {
      console.log("App-level URL check: Special URL pattern found. Redirecting now.");
      redirectToOmoduduwa();
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <RootRedirectHandler>
        {!isImmersiveRoute && <Navbar />}
        <main className={isImmersiveRoute ? "h-screen" : "flex-grow"}>
          <PageTransition>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/mission" element={<MissionPage />} />
                <Route path="/get-involved" element={<GetInvolvedPage />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/news/:id" element={<NewsDetailPage />} />
                <Route path="/resources" element={<Navigate to="/library" replace />} />
                <Route path="/library" element={<LibraryArchivesPage />} />
                <Route path="/academy/path/:pathId" element={<LearningPathDetailPage />} />
                <Route path="/academy/room/:roomId" element={<AcademyRoomPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/join" element={<JoinPage />} />
                <Route path="/community" element={<CommunityPage />} />
                <Route path="/community/hub/:topicId" element={<TopicDetail />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/auth/verify" element={<AuthVerifyPage />} />
                <Route path="/user-profile/:id" element={<ProfileSharePage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </PageTransition>
        </main>
        {!isImmersiveRoute && <Footer />}
        {!isImmersiveRoute && <ProverbWidget />}
      </RootRedirectHandler>
    </div>
  );
};

const App = () => {
  // Final check at the very root level
  useEffect(() => {
    const currentUrl = window.location.href;
    if (currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195')) {
      console.log("Root-level URL check: Special URL pattern found. Redirecting now.");
      redirectToOmoduduwa();
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
